import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
  Paper,
  TextField,
  MenuItem,
  TablePagination,
  Drawer,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import Menu from "@mui/material/Menu";

const TRELLO_API_KEY = "18d50606d419c001df43c639bef31408";
const TRELLO_TOKEN = "f847d5371155e21120eb8c5f1ef5b57a22fa331789125143db766cbd147290b3";

const DEFAULT_BOARD_NAME = "AppJingle Editors";
const DEFAULT_LIST_NAME = "Verified - Fixed";
const TO_PAY_LIST_NAME = "Sign Payment - To Pay";

const TASK_IDS = {
  VA: "Virtual Assistant",
  QE: "Quiz Editor",
  AE: "Article Editor",
  LB: "Link Building",
  PS: "PDF Scrapper",
  OP: "Octoparse Scrapping",
};

const TASK_CONFIG = {
  VA: "time",
  QE: "quantity",
  AE: "time",
  LB: "time",
  PS: "time",
  OP: "time"
};

const SignPayment = () => {
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [taskType, setTaskType] = useState("");
  const [timeSpent, setTimeSpent] = useState("");
  const [quantity, setQuantity] = useState("");
  const [toPayListId, setToPayListId] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch boards
        const boardsResponse = await fetch(
          `https://api.trello.com/1/members/me/boards?key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`
        );
        const boardsData = await boardsResponse.json();
        const defaultBoard = boardsData.find(
          (board) => board.name === DEFAULT_BOARD_NAME
        );

        if (defaultBoard) {
          // Fetch lists for the board
          const listsResponse = await fetch(
            `https://api.trello.com/1/boards/${defaultBoard.id}/lists?key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`
          );
          const listsData = await listsResponse.json();
          setToPayListId(
            listsData.find((list) => list.name === TO_PAY_LIST_NAME)?.id || ""
          );
          const defaultList = listsData.find(
            (list) => list.name === DEFAULT_LIST_NAME
          );

          if (defaultList) {
            // Fetch cards from the list
            const cardsResponse = await fetch(
              `https://api.trello.com/1/lists/${defaultList.id}/cards?key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`
            );
            const cardsData = await cardsResponse.json();
            setCards(cardsData);
            setFilteredCards(cardsData);

            // Fetch all board members
            const boardMembersResponse = await fetch(
              `https://api.trello.com/1/boards/${defaultBoard.id}/members?key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`
            );
            const boardMembersData = await boardMembersResponse.json();
            setMembers(boardMembersData);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleMemberChange = (event) => {
    setSelectedMember(event.target.value);
    filterCards(event.target.value, searchQuery);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    filterCards(selectedMember, event.target.value);
  };

  const filterCards = (memberId, query) => {
    let filtered = cards;

    if (memberId) {
      filtered = filtered.filter((card) => card.idMembers.includes(memberId));
    }

    if (query) {
      filtered = filtered.filter((card) =>
        card.name.toLowerCase().includes(query.toLowerCase())
      );
    }

    setFilteredCards(filtered);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, card) => {
    setAnchorEl(event.currentTarget);
    setSelectedCard(card);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignPaymentClick = () => {
    setTaskType("");
    setTimeSpent("");
    setQuantity("");
    setSidebarOpen(true);
    handleMenuClose();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubmit = async () => {
    if (!taskType || (!timeSpent && !quantity)) {
      setSnackbarMessage("Please fill in all required fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const comment =
      TASK_CONFIG[taskType] === "quantity"
        ? `2PAY-${taskType}-${quantity}I`
        : `2PAY-${taskType}-${timeSpent}`;

    try {
      await fetch(
        `https://api.trello.com/1/cards/${selectedCard.id}/actions/comments?text=${encodeURIComponent(
          comment
        )}&key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`,
        { method: "POST" }
      );

      if (toPayListId) {
        await fetch(
          `https://api.trello.com/1/cards/${selectedCard.id}?idList=${toPayListId}&key=${TRELLO_API_KEY}&token=${TRELLO_TOKEN}`,
          { method: "PUT" }
        );
      }

      setCards((prevCards) =>
        prevCards.filter((card) => card.id !== selectedCard.id)
      );
      setFilteredCards((prevCards) =>
        prevCards.filter((card) => card.id !== selectedCard.id)
      );

      setSnackbarMessage("Payment signed and card moved successfully.");
      setSnackbarSeverity("success");
      setSidebarOpen(false);
    } catch (error) {
      console.error("Error signing payment:", error);
      setSnackbarMessage("Failed to sign payment.");
      setSnackbarSeverity("error");
    } finally {
      setSnackbarOpen(true);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sign Payment
      </Typography>

      <Box display="flex" gap={2} mb={2}>
        <TextField
          label="Search by Title"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <TextField
          select
          label="Select Member"
          fullWidth
          value={selectedMember}
          onChange={handleMemberChange}
        >
          <MenuItem value="">All Members</MenuItem>
          {members.map((member) => (
            <MenuItem key={member.id} value={member.id}>
              {member.fullName}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Member</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCards
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((card) => (
                <TableRow key={card.id}>
                  <TableCell>{card.name}</TableCell>
                  <TableCell>
                    {card.idMembers
                      .map((id) =>
                        members.find((member) => member.id === id)?.fullName
                      )
                      .join(", ")}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={(e) => handleMenuOpen(e, card)}>
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleSignPaymentClick}>
                        Sign Payment
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredCards.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{ width: 300, "& .MuiDrawer-paper": { width: 300, padding: 2 } }}
      >
        <Typography variant="h6" gutterBottom>
          Sign Payment
        </Typography>
        <TextField
          select
          label="Select Task"
          fullWidth
          value={taskType}
          onChange={(e) => setTaskType(e.target.value)}
          sx={{ mb: 2 }}
        >
          {Object.keys(TASK_IDS).map((key) => (
            <MenuItem key={key} value={key}>
              {TASK_IDS[key]}
            </MenuItem>
          ))}
        </TextField>
        {taskType && (
          <>
            {TASK_CONFIG[taskType] === "time" ? (
              <TextField
                label="Time Spent (e.g., 1H or 3M)"
                fullWidth
                value={timeSpent}
                onChange={(e) => setTimeSpent(e.target.value)}
                sx={{ mb: 2 }}
              />
            ) : (
              <TextField
                label="Quantity (e.g., 12)"
                fullWidth
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
          </>
        )}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Drawer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignPayment;
